import { createContext, useContext, useEffect, useState } from "react";
import styles from "./AuthContext.module.css";

export const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";
export const SITE_URL = process.env.REACT_APP_SITE_URL || "http://localhost:3000";
const TOKEN_KEY = "pf_token";

const AuthContext = createContext(null);

function readStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage blocked (private mode etc.) – the session just won't persist
  }
}

// The marketing site hands over the token as `#token=...` after login. The fragment is
// never sent to a server, and we strip it from the address bar straight away.
function takeTokenFromUrl() {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("token");
  if (!token) return null;

  params.delete("token");
  const rest = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + window.location.search + (rest ? `#${rest}` : "")
  );
  return token;
}

const LOGIN_URL = `${SITE_URL}/login`;

export function redirectToLogin() {
  // Inside an embedded preview/iframe, navigate the whole window rather than just the frame
  try {
    if (window.top !== window.self) {
      window.top.location.href = LOGIN_URL;
      return;
    }
  } catch {
    // Cross-origin parent that blocks top navigation – fall through
  }
  window.location.replace(LOGIN_URL);
}

export function AuthProvider({ children }) {
  const [token] = useState(() => {
    const fromUrl = takeTokenFromUrl();
    if (fromUrl) storeToken(fromUrl);
    return fromUrl || readStoredToken();
  });
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [loggedOut, setLoggedOut] = useState(false);

  useEffect(() => {
    if (!token) {
      redirectToLogin();
      return;
    }

    const controller = new AbortController();
    fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403 || res.status === 404) {
          storeToken(null);
          redirectToLogin();
          return;
        }
        if (!res.ok) throw new Error(`Auth check failed (${res.status})`);
        const data = await res.json();
        setUser(data.user);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error(err);
        setError("We couldn't reach the server. Please refresh to try again.");
      });

    return () => controller.abort();
  }, [token]);

  // Saves profile fields (just `username` for now) and updates the user everywhere it's
  // shown. Throws the API's message so the form can show it.
  const updateProfile = async (fields) => {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(fields),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "We couldn't save your changes. Please try again.");
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    storeToken(null);
    // Leave the portal immediately; the notice below stays only if the browser
    // doesn't follow the redirect (some embedded previews block it)
    setLoggedOut(true);
    redirectToLogin();
  };

  if (loggedOut) {
    return (
      <main className={styles.notice}>
        <p>You&apos;ve been logged out.</p>
        <a href={LOGIN_URL} className={styles.loginLink}>
          GO TO LOG IN
        </a>
      </main>
    );
  }
  if (error) return <p style={{ padding: 24 }}>{error}</p>;
  // Render nothing until the session is confirmed so the portal never flashes for logged-out visitors
  if (!user) return null;

  return (
    <AuthContext.Provider value={{ user, token, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
